import { autorun, flow, observable, toJS } from "mobx";
import { useEffect } from "react";
import { CvpQueryClient, useCvpQueryClient } from "../api/queryClient";
import { CvPortalTranslationOverride } from "../api/settings/CvPortalTranslationOverride";
import { guid } from "../utils/util";

// translation overrides previewed from the admin settings page
// it got a bit complicated in order to avoid ui flickering

const previewTranslationOverrides = observable.box<
  CvPortalTranslationOverride[]
>([]);

export const previewTranslationOverridesSetIndicator = observable.box<
  string | null
>(null);

const incomingPreviewTranslationOverrides = observable.box<
  CvPortalTranslationOverride[]
>([]);

export function setPreviewTranslationOverrides(
  overrides: CvPortalTranslationOverride[]
) {
  const currentLength = incomingPreviewTranslationOverrides.get().length;
  const newLength = overrides.length;

  if (currentLength === 0 && newLength === 0) return;

  incomingPreviewTranslationOverrides.set(overrides);
}

export function getPreviewTranslationOverrides() {
  return previewTranslationOverrides.get() ?? [];
}

let idCounter = 1;
const handleNewPreviews = flow(function* (cvpQueryClient: CvpQueryClient) {
  const myId = ++idCounter;

  if (
    previewTranslationOverrides.get().length > 0 &&
    incomingPreviewTranslationOverrides.get().length === 0
  ) {
    // there was unsaved translation overrides from admin page,
    // but they are now saved to backend and removed from the preview

    // wait until fetched from backend
    yield cvpQueryClient.invalidateQueries(
      "settingsAPI.getCvPortalTranslationOverrides"
    );
  }

  const imLast = idCounter === myId;

  if (imLast) {
    // avoid race condition
    previewTranslationOverrides.set(incomingPreviewTranslationOverrides.get());
    previewTranslationOverridesSetIndicator.set(guid());
  }
});

// implemented using a hook in order to access cvpQueryClient
export function useRefetchTranslationOverridesAfterAdminSave() {
  const cvpQueryClient = useCvpQueryClient();

  useEffect(() => {
    return autorun(() => {
      incomingPreviewTranslationOverrides.get(); // trigger autorun

      setTimeout(() => {
        handleNewPreviews(cvpQueryClient);
      });
    });
  }, []);
}
